import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Link, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";

const COOKIE_CONSENT_KEY = "cookieConsentAccepted";
const COOKIE_PREFERENCES_KEY = "cookiePreferences";

interface CookiePreferences {
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
}

const CookieConsent: React.FC = () => {
    const [visible, setVisible] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [preferences, setPreferences] = useState<CookiePreferences>({
        necessary: true,
        analytics: false,
        marketing: false
    });

    useEffect(() => {
        const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (!consent) {
            const timer = setTimeout(() => {
                setVisible(true);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, []);

    const acceptAll = () => {
        const allAccepted = {
            necessary: true,
            analytics: true,
            marketing: true
        };
        localStorage.setItem(COOKIE_CONSENT_KEY, "true");
        localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAccepted));
        setVisible(false);

        if (allAccepted.analytics && window.gtag) {
            window.gtag('consent', 'update', {
                'analytics_storage': 'granted'
            });
        }
    };

    const acceptSelected = () => {
        localStorage.setItem(COOKIE_CONSENT_KEY, "true");
        localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
        setVisible(false);

        if (preferences.analytics && window.gtag) {
            window.gtag('consent', 'update', {
                'analytics_storage': 'granted'
            });
        }
    };

    const rejectAll = () => {
        const onlyNecessary = {
            necessary: true,
            analytics: false,
            marketing: false
        };
        localStorage.setItem(COOKIE_CONSENT_KEY, "true");
        localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(onlyNecessary));
        setVisible(false);

        if (window.gtag) {
            window.gtag('consent', 'update', {
                'analytics_storage': 'denied'
            });
        }
    };

    const handlePreferenceChange = (type: keyof CookiePreferences) => {
        if (type === 'necessary') return;
        setPreferences(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    if (!visible) return null;

    return (
        <Box
            sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: "rgba(0, 0, 0, 0.95)",
                color: "#fff",
                p: { xs: 2, md: 3 },
                zIndex: 9999,
                backdropFilter: "blur(10px)",
                borderTop: "1px solid rgba(255, 255, 255, 0.1)"
            }}
        >
            <Box sx={{ maxWidth: 1200, mx: "auto", position: "relative" }}>
                <IconButton
                    onClick={() => setVisible(false)}
                    sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        color: "white",
                        bgcolor: "rgba(255, 255, 255, 0.1)",
                        "&:hover": {
                            bgcolor: "rgba(255, 255, 255, 0.2)"
                        }
                    }}
                >
                    <Close />
                </IconButton>

                <Box sx={{ pr: 4 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            mb: 1,
                            fontSize: { xs: '18px', md: '21px' }
                        }}
                    >
                        We use cookies to enhance your experience
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            mb: 2,
                            color: "rgba(255, 255, 255, 0.9)",
                            fontSize: { xs: "14px", md: "16px" },
                            lineHeight: 1.5
                        }}
                    >
                        We use cookies and similar technologies to provide the best experience on our website.
                        Some cookies are necessary for the site to function, while others help us analyze usage
                        and provide personalized content. By continuing to use our site, you consent to our use of cookies.
                    </Typography>

                    {showDetails && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: "rgba(255, 255, 255, 0.05)", borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                Cookie Preferences:
                            </Typography>

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            Necessary Cookies
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                                            Required for basic site functionality
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: "#4CAF50", fontWeight: 500 }}>
                                        Always Active
                                    </Typography>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            Analytics Cookies
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                                            Help us understand how visitors use our site
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        variant={preferences.analytics ? "contained" : "outlined"}
                                        onClick={() => handlePreferenceChange('analytics')}
                                        sx={{
                                            minWidth: 60,
                                            color: preferences.analytics ? "white" : "rgba(255, 255, 255, 0.8)",
                                            borderColor: "rgba(255, 255, 255, 0.3)",
                                            bgcolor: preferences.analytics ? "#4CAF50" : "transparent"
                                        }}
                                    >
                                        {preferences.analytics ? "ON" : "OFF"}
                                    </Button>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            Marketing Cookies
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                                            Used to personalize ads and content
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        variant={preferences.marketing ? "contained" : "outlined"}
                                        onClick={() => handlePreferenceChange('marketing')}
                                        sx={{
                                            minWidth: 60,
                                            color: preferences.marketing ? "white" : "rgba(255, 255, 255, 0.8)",
                                            borderColor: "rgba(255, 255, 255, 0.3)",
                                            bgcolor: preferences.marketing ? "#4CAF50" : "transparent"
                                        }}
                                    >
                                        {preferences.marketing ? "ON" : "OFF"}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Link
                            href="/privacy-policy"
                            target="_blank"
                            sx={{
                                color: "rgba(255, 255, 255, 0.8)",
                                textDecoration: "underline",
                                fontSize: "14px",
                                "&:hover": {
                                    color: "white"
                                }
                            }}
                        >
                            Privacy Policy
                        </Link>
                        <Typography sx={{ color: "rgba(255, 255, 255, 0.5)" }}>•</Typography>
                        <Link
                            href="/cookie-policy"
                            target="_blank"
                            sx={{
                                color: "rgba(255, 255, 255, 0.8)",
                                textDecoration: "underline",
                                fontSize: "14px",
                                "&:hover": {
                                    color: "white"
                                }
                            }}
                        >
                            Cookie Policy
                        </Link>
                    </Box>

                    <Box sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1.5,
                        alignItems: { xs: "stretch", sm: "center" }
                    }}>
                        <Button
                            onClick={acceptAll}
                            variant="contained"
                            sx={{
                                bgcolor: "#4CAF50",
                                color: "white",
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                "&:hover": {
                                    bgcolor: "#45a049"
                                }
                            }}
                        >
                            Accept All
                        </Button>

                        <Button
                            onClick={() => setShowDetails(!showDetails)}
                            variant="outlined"
                            sx={{
                                borderColor: "rgba(255, 255, 255, 0.3)",
                                color: "rgba(255, 255, 255, 0.9)",
                                fontWeight: 500,
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                "&:hover": {
                                    borderColor: "rgba(255, 255, 255, 0.5)",
                                    bgcolor: "rgba(255, 255, 255, 0.05)"
                                }
                            }}
                        >
                            {showDetails ? "Hide Details" : "Cookie Settings"}
                        </Button>

                        {showDetails && (
                            <Button
                                onClick={acceptSelected}
                                variant="contained"
                                sx={{
                                    bgcolor: "#2196F3",
                                    color: "white",
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1,
                                    borderRadius: 2,
                                    "&:hover": {
                                        bgcolor: "#1976D2"
                                    }
                                }}
                            >
                                Accept Selected
                            </Button>
                        )}

                        <Button
                            onClick={rejectAll}
                            variant="text"
                            sx={{
                                color: "rgba(255, 255, 255, 0.7)",
                                fontWeight: 500,
                                px: 2,
                                py: 1,
                                "&:hover": {
                                    color: "white",
                                    bgcolor: "rgba(255, 255, 255, 0.05)"
                                }
                            }}
                        >
                            Reject All
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default CookieConsent;